/**
 * ExpenseFab.js
 * Bottom sheet «Новый расход». Кнопку не рендерит — управляется через FabSpeedDial.
 *
 * Props:
 *   propertyId   — предзаполняет форму (привязка к объекту)
 *   propertyName — название объекта для отображения в пилюле
 *   clientId     — предзаполняет форму (привязка к клиенту)
 *   clientName   — название клиента для отображения в пилюле
 *   onCreated    — callback после создания расхода (опционально)
 *   openSheet    — управляемое открытие bottom sheet
 *   onSheetClose — callback закрытия
 *   allowPropertyPick — разрешить выбор объекта, когда контекст не задан (напр. с Дашборда)
 */
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { usePerm } from '../hooks/usePerm';
import { AddExpenseCard, uploadReceiptToR2 } from './ExpensesSection';
import { PropertyPickerInline } from './LinkPickers';
import { track } from '../analytics';
import { backdropClose } from '../utils/modalBackdrop';
import useAssignable from '../hooks/useAssignable';

export default function ExpenseFab({ propertyId, propertyName, clientId, clientName, onCreated, openSheet = false, onSheetClose, allowPropertyPick = false }) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { isOwnScope } = usePerm();
  // Совпадает с бэкендом (expenses.js isManager): чужие расходы модерирует
  // роль с областью шире собственных Работ. Какие именно записи ей видны,
  // решает сама область данных.
  const canManage = !isOwnScope;
  const [open, setOpen] = useState(false);
  const [categories,  setCategories]  = useState([]);
  // «Исполнитель» расхода — /api/team/assignable (у Бригадира /settings/team давал 403)
  const assignable = useAssignable(canManage);
  const teamMembers = assignable.users;
  const [properties,  setProperties]  = useState([]);

  // Активная привязка — инициализируется из пропов при каждом открытии
  const [activePropertyId, setActivePropertyId] = useState(propertyId || null);
  const [activeClientId,   setActiveClientId]   = useState(clientId   || null);
  // Имя объекта, выбранного через пикер (когда привязка не пришла в пропах)
  const [pickedPropertyName, setPickedPropertyName] = useState(null);

  // Пикер объекта доступен только когда контекст не задан и юзер — менеджер
  const canPickProperty = allowPropertyPick && canManage && !propertyId && !clientId;

  // Синхронизируем с управляемым стейтом + сбрасываем привязку при каждом открытии
  useEffect(() => {
    setOpen(openSheet);
    if (openSheet) {
      setActivePropertyId(propertyId || null);
      setActiveClientId(clientId   || null);
      setPickedPropertyName(null);
    }
  }, [openSheet, propertyId, clientId]);

  // Список объектов для пикера — грузим один раз при первом открытии
  useEffect(() => {
    if (!open || !canPickProperty || properties.length) return;
    api.getProperties()
      .then(list => setProperties(list || []))
      .catch(() => {});
  }, [open, canPickProperty, properties.length]);

  // Закрытие по Escape (как у остальных модалов) — через guard
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line
  }, [open]);

  // Категории грузим при монтировании и при смене языка
  useEffect(() => {
    api.get('/expenses/categories')
      .then(cats => setCategories((cats || []).map(c => ({
        ...c,
        name: c.translations?.[i18n.language]?.name || c.name,
      }))))
      .catch(() => {});
  }, [i18n.language]);

  // Закрытие без проверок (после сохранения / явной «Отмены», где черновик уже очищен)
  const rawClose = () => {
    setOpen(false);
    onSheetClose && onSheetClose();
  };

  // Защита от случайного закрытия: если форма расхода «грязная» — подтверждаем.
  // Черновик при закрытии сохраняется (чистится только по «Отмена» внутри карточки).
  const guardRef = useRef({ isDirty: false });
  const handleClose = () => {
    if (guardRef.current?.isDirty) {
      track('modal_close_guard_shown', { scope: 'expense' });
      // eslint-disable-next-line no-alert
      if (!window.confirm(t('common.draftCloseConfirm'))) return;
    }
    rawClose();
  };

  const handleRemoveContext = () => {
    setActivePropertyId(null);
    setActiveClientId(null);
    setPickedPropertyName(null);
    track('expense_fab_context_removed', {
      had_property: !!activePropertyId,
      had_client:   !!activeClientId,
    });
  };

  if (!open) return null;

  // Есть ли активная привязка
  const hasContext = !!(activePropertyId || activeClientId);
  const activePropertyName = pickedPropertyName || propertyName || activePropertyId;
  const contextLabel = activePropertyId
    ? `${t('expenses.contextProperty')}: ${activePropertyName}`
    : activeClientId
      ? `${t('expenses.contextClient')}: ${clientName || activeClientId}`
      : null;

  // Привязка была в пропах, но уже сброшена
  const hadContextOriginally = !!(propertyId || clientId);
  const contextWasRemoved = hadContextOriginally && !hasContext;

  // Пилюля активной привязки (объект/клиент) — общая для всех сценариев
  const activePill = (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '5px 10px 5px 12px',
      borderRadius: 20,
      background: 'rgba(79,142,247,0.12)',
      border: '1px solid var(--accent, #6366f1)',
      fontSize: 13, fontWeight: 500,
      color: 'var(--accent, #6366f1)',
      maxWidth: '100%',
    }}>
      <span style={{ fontSize: 14, flexShrink: 0 }}>
        {activePropertyId ? '📍' : '👤'}
      </span>
      <span style={{
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        maxWidth: 'calc(100vw - 120px)',
      }}>
        {contextLabel}
      </span>
      <button
        onClick={handleRemoveContext}
        title={t('expenses.removeContext')}
        style={{
          flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 18, height: 18, borderRadius: '50%',
          border: 'none',
          background: 'var(--accent, #6366f1)',
          color: '#fff',
          cursor: 'pointer',
          fontSize: 11, fontWeight: 700, lineHeight: 1,
          marginLeft: 2,
        }}
      >×</button>
    </div>
  );

  // Пилюля контекста (привязка к объекту/клиенту) — рендерим в шапке тела модала.
  // Три сценария:
  //   1) контекст был в пропах (карточка объекта/клиента) — пилюля + «Общий расход» при сбросе;
  //   2) контекст не задан, но разрешён пикер (Дашборд) — активная пилюля или выбор объекта;
  //   3) иначе — ничего.
  let contextPill = null;
  if (hadContextOriginally) {
    contextPill = (
      <div style={{ marginBottom: 14 }}>
        {hasContext ? activePill : (
          /* Привязка сброшена — показываем "Общий расход" + кнопку вернуть */
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '5px 12px',
            borderRadius: 20,
            background: 'var(--bg3)',
            border: '1px solid var(--border)',
            fontSize: 13, color: 'var(--text3)',
          }}>
            <span>🧾</span>
            <span>{t('expenses.contextGeneral')}</span>
            <button
              onClick={() => {
                setActivePropertyId(propertyId || null);
                setActiveClientId(clientId || null);
              }}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--accent, #6366f1)', fontSize: 12, fontWeight: 600,
                padding: '0 2px',
              }}
            >↩</button>
          </div>
        )}
      </div>
    );
  } else if (canPickProperty) {
    contextPill = (
      <div style={{ marginBottom: 14 }}>
        {hasContext ? activePill : (
          <PropertyPickerInline
            properties={properties}
            onPick={(p) => {
              setActivePropertyId(p.id);
              setPickedPropertyName(p.name);
              track('expense_fab_property_picked', { property_id: p.id });
            }}
            t={t}
          />
        )}
      </div>
    );
  }

  return createPortal(
    <div className="modal-overlay" {...backdropClose(handleClose)}>
      <div className="modal">
        <div className="modal-header">
          <h3>{t('expenses.newExpense')}</h3>
          <button className="modal-close" onClick={handleClose}>×</button>
        </div>
        <AddExpenseCard
          variant="modal"
          contextSlot={contextPill}
          onCancel={rawClose}
          categories={categories}
          initialOpen={true}
          propertyId={activePropertyId}
          clientId={activeClientId}
          onFileUpload={uploadReceiptToR2}
          canManage={canManage}
          teamMembers={teamMembers}
          teams={assignable.teams}
          draftScope={`expense:${activePropertyId ? 'prop' + activePropertyId : activeClientId ? 'client' + activeClientId : 'general'}`}
          guardRef={guardRef}
          onCreated={(exp) => {
            rawClose();
            onCreated && onCreated(exp);
            track('expense_created', {
              source:       'fab',
              has_property: !!activePropertyId,
              has_client:   !!activeClientId,
              context_removed: contextWasRemoved,
            });
          }}
        />
      </div>
    </div>,
    document.body
  );
}
